import { View, StyleSheet, Pressable } from "react-native";
import { router, usePathname } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";

interface ShiftSupervisor {
  id: string;
  user_id: string;
  user?: {
    id: string;
    first_name: string;
    last_name: string;
    email_address?: string;
    preferred_name?: string;
  };
}

interface SupervisorsTabProps {
  supervisors?: ShiftSupervisor[];
  shiftId?: string;
}

export function SupervisorsTab({ supervisors, shiftId }: SupervisorsTabProps) {
  const { colors } = useTheme();
  const pathname = usePathname();

  if (!supervisors || supervisors.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <MaterialCommunityIcons name="account-tie" size={48} color={colors.placeholder} />
        <ThemedText style={[styles.emptyText, { color: colors.textSecondary }]}>No supervisors assigned</ThemedText>
      </View>
    );
  }

  const handleSupervisorPress = (userId: string) => {
    if (!shiftId) return;
    router.navigate(
      pathname.includes("/crew/")
        ? { pathname: "/crew/home/[shift]/supervisor/[supervisor]", params: { shift: shiftId, supervisor: userId } }
        : { pathname: "/home/[shift]/supervisor/[supervisor]", params: { shift: shiftId, supervisor: userId } },
    );
  };

  return (
    <View style={styles.container}>
      {supervisors.map((supervisor) => {
        const user = supervisor.user;
        const displayName = user
          ? user.preferred_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email_address
          : "Unknown Supervisor";

        return (
          <Pressable
            key={supervisor.id}
            onPress={() => handleSupervisorPress(supervisor.user_id)}
            style={({ pressed, hovered }) => [
              styles.supervisorItem,
              { backgroundColor: colors.backgroundSecondary, borderColor: colors.border, cursor: "pointer" } as any,
              hovered && { backgroundColor: colors.card },
              pressed && { opacity: 0.7 },
            ]}
          >
            <Avatar userId={user?.id} firstName={user?.first_name} lastName={user?.last_name} size={36} />
            <View style={styles.supervisorInfo}>
              <ThemedText style={styles.supervisorName}>{displayName}</ThemedText>
              {user?.email_address && user.email_address !== displayName && (
                <ThemedText style={[styles.supervisorEmail, { color: colors.textSecondary }]}>
                  {user.email_address}
                </ThemedText>
              )}
            </View>
            <View
              style={[styles.badge, { backgroundColor: colors.amber?.background, borderColor: colors.amber?.border }]}
            >
              <ThemedText style={[styles.badgeText, { color: colors.amber?.text }]}>Supervisor</ThemedText>
            </View>
            <MaterialCommunityIcons name="chevron-right" size={20} color={colors.textSecondary} />
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 16,
    gap: 8,
  },
  emptyContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    gap: 8,
  },
  emptyText: {
    fontSize: 14,
  },
  supervisorItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    padding: 12,
    borderRadius: 8,
    borderWidth: StyleSheet.hairlineWidth,
  },
  supervisorInfo: {
    flex: 1,
    gap: 2,
  },
  supervisorName: {
    fontSize: 14,
    fontWeight: "500",
  },
  supervisorEmail: {
    fontSize: 12,
  },
  badge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
  },
  badgeText: {
    fontSize: 10,
    fontWeight: "600",
  },
});
